import React from 'react';
import { Alert, Icon, theme } from 'components';
import { APPOINTMENT_STATUSES, AppointmentStatus } from '@bb/domain/dist/types';
import { AlertProps } from 'antd';
import errorReporter from '@app/errorReporter';

const invalidStatuses = [
  'removed',
  'config_change',
  'historical',
  'attempted',
  'non_agent_approval',
] as const;
type AppointmentStatusWithBanner =
  | Exclude<AppointmentStatus, (typeof invalidStatuses)[number]>
  | 'complete'
  | 'listingContactTimeChange';

type StatusBannerProps = {
  status: AppointmentStatus;
  userType: 'agent' | 'coop';
  isPassed?: boolean;
  reason?: string;
  listingContactTimeChange?: boolean;
};

const alertStatusContentMap: Record<
  AppointmentStatusWithBanner,
  Record<'agent' | 'coop', { message: string; icon: React.ReactNode }>
> = {
  new: {
    agent: {
      message: 'New showing request',
      icon: (
        <img
          src="https://cdn.filestackcontent.com/b1w4YgQlS5ubAYHaD48H"
          width={15}
          height={15}
        />
      ),
    },
    coop: {
      message: 'Your showing request is awaiting approval',
      icon: (
        <img
          src="https://cdn.filestackcontent.com/8Y7tv9ROQFm2D6EavrUX"
          width={15}
          height={15}
        />
      ),
    },
  },
  time_change: {
    agent: {
      message: 'A new time was requested',
      icon: <Icon type="antdClockCircle" width={15} color="blue6" />,
    },
    coop: {
      message: 'You requested a new time',
      icon: <Icon type="antdSyncOutlined" width={15} color="blue6" />,
    },
  },
  listingContactTimeChange: {
    agent: {
      message: 'You, or a listing Contact, Suggested a New Time.',
      icon: <Icon type="antdClockCircle" width={15} color="blue6" />,
    },
    coop: {
      message: 'You requested a new time',
      icon: <Icon type="antdSyncOutlined" width={15} color="blue6" />,
    },
  },
  suggested_time_change: {
    agent: {
      message: 'You suggested a new time',
      icon: <Icon type="antdSyncOutlined" width={15} color="blue6" />,
    },
    coop: {
      message: 'A new time was suggested',
      icon: <Icon type="antdClockCircle" width={15} color="blue6" />,
    },
  },
  draft: {
    agent: {
      message: 'The showing has been marked as draft',
      icon: <Icon type="antdFileTextOutlined" width={15} color="blue6" />,
    },
    coop: {
      message: 'The showing has been marked as draft',
      icon: <Icon type="antdFileTextOutlined" width={15} color="blue6" />,
    },
  },
  confirm: {
    agent: {
      message: 'The showing request is confirmed',
      icon: <Icon type="checkCircle" width={15} color="success" />,
    },
    coop: {
      message: 'Your showing request is confirmed',
      icon: <Icon type="checkCircle" width={15} color="success" />,
    },
  },
  complete: {
    agent: {
      message: 'The showing is complete',
      icon: <Icon type="checkCircle" width={15} color="success" />,
    },
    coop: {
      message: 'Your showing is complete',
      icon: <Icon type="checkCircle" width={15} color="success" />,
    },
  },
  cancel: {
    agent: {
      message: 'The showing request has been cancelled',
      icon: <Icon type="antdCloseCircleOutlined" width={15} color={'red5'} />,
    },
    coop: {
      message: 'Your showing request is cancelled',
      icon: <Icon type="antdCloseCircleOutlined" width={15} color={'red5'} />,
    },
  },
  deny: {
    agent: {
      message: 'The showing request has been declined',
      icon: <Icon type="antdCloseCircleOutlined" width={15} color={'red5'} />,
    },
    coop: {
      message: 'Your showing request has been declined',
      icon: <Icon type="antdCloseCircleOutlined" width={15} color={'red5'} />,
    },
  },
  time_passed: {
    agent: {
      message: 'The time has passed for this showing request',
      icon: (
        <img
          src="https://cdn.filestackcontent.com/HQFoBH5DT4eqvuD2K7MC"
          width={15}
          height={15}
        />
      ),
    },
    coop: {
      message: 'The time has passed for this showing request',
      icon: (
        <img
          src="https://cdn.filestackcontent.com/HQFoBH5DT4eqvuD2K7MC"
          width={15}
          height={15}
        />
      ),
    },
  },
};

const getAlertProps = ({
  status,
  userType,
  isPassed,
  reason,
  listingContactTimeChange,
}: {
  status: AppointmentStatusWithBanner;
  userType: 'agent' | 'coop';
  isPassed?: boolean;
  reason?: string;
  listingContactTimeChange?: boolean;
}) => {
  let style: React.CSSProperties;
  let type: AlertProps['type'];

  // Set type, style
  switch (status) {
    case 'new':
    case 'time_change':
    case 'suggested_time_change':
      type = 'info';
      style = {
        backgroundColor: theme.blue1,
        border: `1px solid ${theme.blue3}`,
      };
      break;
    case 'confirm':
    case 'complete':
      type = 'success';
      style = {
        backgroundColor: theme.green1,
        border: `1px solid ${theme.green3}`,
      };
      break;
    case 'cancel':
    case 'deny':
      type = 'error';
      style = {
        backgroundColor: theme.red1,
        border: `1px solid ${theme.red3}`,
      };
      break;
    case 'time_passed':
      type = 'success';
      style = {
        backgroundColor: theme.gray2,
        border: `1px solid ${theme.gray5}`,
      };
      break;
    default:
      errorReporter.error(
        `Error creating status banner: invalid status ${status}`,
      );
  }

  let convertedStatus: AppointmentStatusWithBanner = status;
  if (status === 'confirm' && isPassed) {
    convertedStatus = 'complete';
  } else if (status === 'time_change' && listingContactTimeChange) {
    convertedStatus = 'listingContactTimeChange';
  }

  // Set message, icon
  const alertContent = alertStatusContentMap[convertedStatus][userType];

  const { icon } = alertContent;
  let { message } = alertContent;

  // Append reason if provided
  if (['cancel', 'deny'].includes(status) && reason) {
    message += ` because "${reason}"`;
  }

  return { style, message, type, icon };
};

const isValidStatus = (status: any): status is AppointmentStatusWithBanner =>
  APPOINTMENT_STATUSES.includes(status) && !invalidStatuses.includes(status);

const StatusBanner: React.FC<StatusBannerProps> = ({
  status,
  userType,
  reason,
  isPassed,
  listingContactTimeChange,
}) => {
  if (!isValidStatus(status)) {
    return null;
  }

  return (
    <Alert
      data-status={status}
      showIcon
      {...getAlertProps({
        userType,
        status,
        reason,
        isPassed,
        listingContactTimeChange,
      })}
    />
  );
};

export default StatusBanner;
